import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { selectCartItems, selectCartTotal, removeFromCart, updateQuantity } from '../store/slices/cartSlice';
import { selectNairaPerYuan, toDisplayNaira, formatNaira } from '../store/slices/currencySlice';
import { SERVER_BASE_URL } from '../services/api';
import Layout from '../components/Layout';
import './Cart.css';

const getImageUrl = (image) => {
  if (!image) return null;
  if (typeof image !== 'string') return null;
  if (image.startsWith('http://') || image.startsWith('https://')) return image;
  return `${SERVER_BASE_URL}${image.startsWith('/') ? '' : '/'}${image}`;
};

const Cart = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const cartItems = useAppSelector(selectCartItems);
  const cartTotalRmb = useAppSelector(selectCartTotal);
  const nairaPerYuan = useAppSelector(selectNairaPerYuan);

  const displayTotal = toDisplayNaira(cartTotalRmb, nairaPerYuan);

  const handleRemove = (item) => {
    const itemId = item.sizeId ? `${item.id}-${item.sizeId}` : item.id;
    dispatch(removeFromCart(itemId));
  };

  const handleQuantityChange = (item, newQuantity) => {
    const itemId = item.sizeId ? `${item.id}-${item.sizeId}` : item.id;
    dispatch(updateQuantity({ id: itemId, quantity: newQuantity }));
  };

  const handleContinue = () => {
    if (cartItems.length > 0) {
      navigate('/order');
    }
  };

  const formatPrice = (priceInYuan) => {
    return formatNaira(toDisplayNaira(priceInYuan, nairaPerYuan));
  };

  if (cartItems.length === 0) {
    return (
      <Layout>
        <div className="cart-page">
          <div className="cart-container">
            <h1>Your Cart</h1>
            <div className="empty-cart">
              <p>Your cart is empty</p>
              <button onClick={() => navigate('/products')} className="shop-btn">
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="cart-page">
        <div className="cart-container">
          <h1>Your Cart</h1>
          
          <div className="cart-content">
            <div className="cart-items">
              {cartItems.map((item, index) => {
                const uniqueKey = item.sizeId ? `${item.id}-${item.sizeId}` : item.id;
                const imageUrl = getImageUrl(item.image);
                return (
                  <div key={uniqueKey || index} className="cart-item">
                  <div className="cart-item-image">
                    {imageUrl ? (
                      <img src={imageUrl} alt={item.name || 'Product'} />
                    ) : (
                      <div className="cart-item-image-placeholder">No image</div>
                    )}
                  </div>
                  
                  <div className="cart-item-details">
                    <h3>{item.name || 'Product'}</h3>
                    {item.selectedSize && (
                      <p className="cart-item-size">Variant: {item.selectedSize}</p>
                    )}
                    <p className="cart-item-description">{item.subtitle || item.description || ''}</p>
                    <p className="cart-item-price">{formatPrice(item.price || 0)}</p>
                  </div>
                  
                  <div className="cart-item-controls">
                    <div className="quantity-controls">
                      <button 
                        onClick={() => handleQuantityChange(item, item.quantity - 1)}
                        className="quantity-btn"
                      >
                        -
                      </button>
                      <span className="quantity">{item.quantity}</span>
                      <button 
                        onClick={() => handleQuantityChange(item, item.quantity + 1)}
                        className="quantity-btn"
                      >
                        +
                      </button>
                    </div>
                    
                    <button 
                      onClick={() => handleRemove(item)}
                      className="remove-btn"
                    >
                      Remove
                    </button>
                  </div>
                  
                  <div className="cart-item-total">
                    {formatPrice((item.price || 0) * item.quantity)}
                  </div>
                  </div>
                );
              })}
            </div>
            
            <div className="cart-summary">
              <h2>Order Summary</h2>
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>{formatNaira(displayTotal)}</span>
              </div>
              <div className="summary-row">
                <span>Shipping:</span>
                <span>Calculated at checkout</span>
              </div>
              <div className="summary-row total">
                <span>Total:</span>
                <span>{formatNaira(displayTotal)}</span>
              </div>
              
              <button onClick={handleContinue} className="continue-btn">
                Continue to Checkout
              </button>
              
              <button onClick={() => navigate('/products')} className="continue-shopping-btn">
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Cart;
